/**
 * Diagnostics for minimized sidebar flyout labels (`.sidebar-minimized-flyout-label`
 * + `data-sidebar-label` on the anchor).
 *
 * In development builds, detailed probes run by default. Turn off:
 *   localStorage.setItem("sidebarMinimizedLabelDebug", "0"); location.reload();
 *
 * In production, turn on:
 *   localStorage.setItem("sidebarMinimizedLabelDebug", "1"); location.reload();
 * or URL / hash: sidebarLabelDebug=1
 */

const STORAGE_KEY = "sidebarMinimizedLabelDebug";

export const isSidebarMinimizedLabelDebugEnabled = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored === "0") {
      return false;
    }
    if (stored === "1") {
      return true;
    }
    const { search, hash } = window.location;
    if (/\bsidebarLabelDebug=1\b/.test(search)) {
      return true;
    }
    if (/\bsidebarLabelDebug=1\b/.test(hash)) {
      return true;
    }
  } catch {
    return false;
  }
  return process.env.NODE_ENV === "development";
};

const prefix = "[SidebarMinimizedLabelDebug]";

export const logSidebarMinimizedLabelDebug = (
  phase: string,
  detail: Record<string, unknown>,
): void => {
  if (!isSidebarMinimizedLabelDebugEnabled()) {
    return;
  }
  console.info(prefix, phase, detail);
};

const probeAnchor = (
  el: HTMLElement,
  label: string,
): Record<string, unknown> => {
  const cs = window.getComputedStyle(el);
  let after: CSSStyleDeclaration | null = null;
  try {
    after = window.getComputedStyle(el, "::after");
  } catch {
    after = null;
  }
  const li = el.closest("li.nav-item");
  const liCs = li ? window.getComputedStyle(li) : null;
  const rail = el.closest(".sidebar.sidebar-modernized");
  const railCs = rail
    ? window.getComputedStyle(rail as HTMLElement)
    : null;
  const rect = el.getBoundingClientRect();
  const flyout = el.querySelector(".sidebar-minimized-flyout-label");
  const flyoutCs = flyout ? window.getComputedStyle(flyout) : null;
  const flyoutRectEl = flyout ? flyout.getBoundingClientRect() : null;

  return {
    label,
    tag: el.tagName,
    className: el.className,
    hasDataSidebarLabel: el.hasAttribute("data-sidebar-label"),
    dataSidebarLabelAttr: el.getAttribute("data-sidebar-label"),
    ariaLabel: el.getAttribute("aria-label"),
    inlineOverflow: (el as HTMLElement).style.overflow || null,
    rect: {
      width: Math.round(rect.width),
      height: Math.round(rect.height),
      left: Math.round(rect.left),
      top: Math.round(rect.top),
    },
    computedLink: {
      overflow: cs.overflow,
      overflowX: cs.overflowX,
      overflowY: cs.overflowY,
      position: cs.position,
      zIndex: cs.zIndex,
      fontSize: cs.fontSize,
      textIndent: cs.textIndent,
      visibility: cs.visibility,
      opacity: cs.opacity,
      clipPath: cs.clipPath,
    },
    computedAfter: after
      ? {
          content: after.content,
          opacity: after.opacity,
          visibility: after.visibility,
          display: after.display,
          position: after.position,
          left: after.left,
          top: after.top,
          fontSize: after.fontSize,
        }
      : null,
    parentLi: li
      ? {
          overflow: liCs?.overflow,
          overflowX: liCs?.overflowX,
          overflowY: liCs?.overflowY,
          dataSidebarLabelAnchor: li.getAttribute(
            "data-sidebar-label-anchor",
          ),
        }
      : null,
    sidebarRail: rail
      ? {
          overflow: railCs?.overflow,
          overflowX: railCs?.overflowX,
          overflowY: railCs?.overflowY,
          className: (rail as HTMLElement).className,
        }
      : null,
    flyoutDomPresent: Boolean(flyout),
    flyoutBoundingRect: flyoutRectEl
      ? {
          width: Math.round(flyoutRectEl.width),
          height: Math.round(flyoutRectEl.height),
          left: Math.round(flyoutRectEl.left),
          top: Math.round(flyoutRectEl.top),
          right: Math.round(flyoutRectEl.right),
          bottom: Math.round(flyoutRectEl.bottom),
        }
      : null,
    bodySidebarMinimized:
      typeof document !== "undefined" &&
      document.body.classList.contains("sidebar-minimized"),
    flyoutSpan: flyoutCs
      ? {
          display: flyoutCs.display,
          opacity: flyoutCs.opacity,
          visibility: flyoutCs.visibility,
          position: flyoutCs.position,
          left: flyoutCs.left,
          zIndex: flyoutCs.zIndex,
        }
      : null,
  };
};

/** Full DOM snapshot after minimize toggle (run after layout). */
export const snapshotMinimizedLabelDom = (reason: string): void => {
  if (!isSidebarMinimizedLabelDebugEnabled()) {
    return;
  }

  const bodyMin = document.body.classList.contains("sidebar-minimized");
  const rail = document.querySelector(".sidebar.sidebar-modernized");
  const labeled = document.querySelectorAll(
    ".sidebar.sidebar-modernized a.nav-link[data-sidebar-label], .sidebar.sidebar-modernized .nav-link[data-sidebar-label]",
  );

  const samples: Record<string, unknown>[] = [];
  labeled.forEach((node, i) => {
    if (i >= 5) {
      return;
    }
    const el = node as HTMLElement;
    const name =
      el.getAttribute("data-sidebar-label") ||
      el.getAttribute("aria-label") ||
      `(index ${i})`;
    samples.push(probeAnchor(el, name));
  });

  let stylesheetRulesMentioningLabel = 0;
  try {
    const sheets = Array.from(document.styleSheets);
    for (const sheet of sheets) {
      let rulesList: CSSRule[];
      try {
        rulesList = Array.from(sheet.cssRules ?? []);
      } catch {
        continue;
      }
      for (const rule of rulesList) {
        if (
          rule instanceof CSSStyleRule &&
          rule.selectorText.includes("data-sidebar-label")
        ) {
          stylesheetRulesMentioningLabel++;
        }
      }
    }
  } catch {
    stylesheetRulesMentioningLabel = -1;
  }

  const payload = {
    reason,
    bodySidebarMinimized: bodyMin,
    contextNote:
      "If bodySidebarMinimized is false here but React thinks minimized, body class and context are out of sync.",
    railFound: Boolean(rail),
    railClassName: rail ? (rail as HTMLElement).className : null,
    labeledLinkCount: labeled.length,
    samples,
    stylesheetRulesMentioningDataSidebarLabel:
      stylesheetRulesMentioningLabel,
    hintFontFailures:
      "Console 'Failed to decode downloaded font' / OTS errors can make FA icons invisible; labels use normal fonts and should still show if CSS applies.",
  };

  console.info(prefix, "snapshot", payload);

  if (bodyMin && labeled.length === 0) {
    console.warn(
      prefix,
      "While body has sidebar-minimized, zero elements matched .sidebar.sidebar-modernized … [data-sidebar-label]. Check rail classes or that NavLink/NavDropdown minimized props ran.",
      { railSelector: ".sidebar.sidebar-modernized" },
    );
  }

  if (stylesheetRulesMentioningLabel === 0) {
    console.warn(
      prefix,
      "No CSSStyleRule selectors containing 'data-sidebar-label' were found in readable stylesheets. _sidebar-modernized.scss may not be loaded in this bundle, or rules live in an inaccessible (cross-origin) sheet.",
    );
  }
};

export const logHoverMinimizedLabelProbe = (
  el: HTMLElement,
  itemName: string,
): void => {
  if (!isSidebarMinimizedLabelDebugEnabled()) {
    return;
  }
  console.info(prefix, "hover-probe", probeAnchor(el, itemName));
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      console.info(
        prefix,
        "hover-probe-after-layout",
        probeAnchor(el, itemName),
      );
    });
  });
};
